import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUS_STYLES = {
  pending: "text-amber-400 border-amber-400/30",
  contacted: "text-sky-400 border-sky-400/30",
  confirmed: "text-emerald-400 border-emerald-400/30",
  declined: "text-zinc-500 border-zinc-600",
};

export default function Admin() {
  const [auth, setAuth] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data.bookings);
    } catch {
      toast.error("Could not load bookings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setAuth(data);
      } catch {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/auth/me");
          setAuth(data);
        } catch {
          setAuth(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (auth) loadBookings();
  }, [auth, loadBookings]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAuth(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setAuth(false);
  };

  const setStatus = async (bookingId, status) => {
    try {
      await api.patch(`/admin/bookings/${bookingId}`, { status });
      setBookings((bs) => bs.map((b) => (b.booking_id === bookingId ? { ...b, status } : b)));
      toast.success(`Marked ${status}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  if (auth === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian" data-testid="admin-loading">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="spotlight-card w-full max-w-md p-10">
          <div className="mb-8 flex justify-center"><Logo /></div>
          <h1 className="text-center font-serif text-2xl text-slate-100">Owner Dashboard</h1>
          <p className="mt-2 text-center text-xs tracking-[0.2em] text-zinc-500 uppercase">Sky Suite Rentals</p>
          <div className="mt-8 space-y-5">
            <div>
              <label className="lux-label" htmlFor="admin-email">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required className="lux-input"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@skysuiteatl.com" />
            </div>
            <div>
              <label className="lux-label" htmlFor="admin-password">Password</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required className="lux-input"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
            <button type="submit" data-testid="admin-login-btn"
              className="w-full rounded-full bg-gold px-6 py-3.5 font-display text-xs font-semibold tracking-[0.25em] text-obsidian uppercase transition-all hover:bg-gold-bright">
              Sign In
            </button>
            <Link to="/" data-testid="admin-back-home-link" className="block text-center text-xs text-zinc-500 transition-colors hover:text-gold">
              ← Back to site
            </Link>
          </div>
        </form>
      </div>
    );
  }

  const revenue = bookings.reduce((s, b) => s + (b.total_price || 0), 0);
  const pending = bookings.filter((b) => b.status === "pending").length;

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <header className="border-b border-gold/15 bg-obsidian/90">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="admin-home-link"><Logo /></Link>
          <div className="flex items-center gap-3">
            <button onClick={loadBookings} data-testid="admin-refresh-btn"
              className="rounded-full border border-gold/30 p-2.5 text-gold transition-colors hover:bg-gold/10" aria-label="Refresh bookings">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-full border border-gold/30 px-5 py-2.5 font-display text-[11px] tracking-[0.2em] text-gold uppercase transition-colors hover:bg-gold/10">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-12">
        <p className="eyebrow">Owner Dashboard</p>
        <h1 className="mt-3 font-serif text-3xl text-slate-100">Booking Enquiries</h1>

        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {[
            { label: "Total Enquiries", value: bookings.length, tid: "stat-total-bookings" },
            { label: "Revenue Potential", value: `$${revenue.toLocaleString()}`, tid: "stat-revenue" },
            { label: "Awaiting Response", value: pending, tid: "stat-pending" },
          ].map((s) => (
            <div key={s.tid} className="spotlight-card p-7" data-testid={s.tid}>
              <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{s.label}</p>
              <p className="mt-3 font-serif text-4xl text-gold">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="spotlight-card mt-10 overflow-x-auto" data-testid="bookings-table">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead>
              <tr className="border-b border-gold/15 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
                <th className="px-6 py-4">Reference</th>
                <th className="px-6 py-4">Guest</th>
                <th className="px-6 py-4">Experience</th>
                <th className="px-6 py-4">Dates</th>
                <th className="px-6 py-4">Total</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && (
                <tr><td colSpan="6" className="px-6 py-14 text-center text-zinc-500" data-testid="bookings-empty">
                  {loading ? "Loading…" : "No booking enquiries yet."}
                </td></tr>
              )}
              {bookings.map((b) => (
                <tr key={b.booking_id} className="border-b border-gold/5 transition-colors hover:bg-gold/5" data-testid={`booking-row-${b.reference}`}>
                  <td className="px-6 py-4 font-mono text-xs text-gold">{b.reference}</td>
                  <td className="px-6 py-4">
                    <p className="text-slate-200">{b.guest_name}</p>
                    <p className="text-xs text-zinc-500">{b.guest_email} · {b.guest_phone}</p>
                  </td>
                  <td className="px-6 py-4 text-zinc-300">{b.item_name}</td>
                  <td className="px-6 py-4 font-mono text-xs text-zinc-400">{b.start_date} → {b.end_date}</td>
                  <td className="px-6 py-4 font-serif text-lg text-slate-100">${b.total_price.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <select
                      data-testid={`booking-status-select-${b.booking_id}`}
                      value={b.status}
                      onChange={(e) => setStatus(b.booking_id, e.target.value)}
                      className={`rounded-full border bg-transparent px-3 py-1.5 font-mono text-[11px] tracking-wider uppercase outline-none [color-scheme:dark] ${STATUS_STYLES[b.status] ?? ""}`}
                    >
                      {Object.keys(STATUS_STYLES).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
